import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ImageIcon } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { assetUrl } from "../../lib/api";
import { formatMoney } from "../../lib/pricing";
import StatusBadge from "../../components/admin/StatusBadge";

export default function AdminYachtList() {
  const [yachts, setYachts] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    setYachts(null);
    adminApi
      .get("/admin/yachts", { params: includeArchived ? { include_archived: true } : {} })
      .then((r) => setYachts(r.data))
      .catch(() => setYachts([]));
  }, [includeArchived]);

  return (
    <div data-testid="admin-yacht-list">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="hbe-eyebrow">Fleet</div>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl">Yachts</h1>
        </div>
        <Link to="/admin/yachts/new" className="hbe-btn-primary" data-testid="yl-add">
          <Plus size={16} className="mr-2" /> Add yacht
        </Link>
      </div>

      <label className="mt-6 inline-flex items-center gap-2 text-sm text-hbe-ink/70">
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
          data-testid="yl-include-archived"
        />
        Show archived yachts
      </label>

      {yachts === null ? (
        <div className="mt-6 text-hbe-ink/50" data-testid="yl-loading">Loading…</div>
      ) : yachts.length === 0 ? (
        <div className="mt-6 hbe-card p-6 text-hbe-ink/60" data-testid="yl-empty">
          No yachts. Click "Add yacht" to create one.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto hbe-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-hbe-ink/55 border-b border-hbe-line">
                <th className="px-4 py-3">Photo</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Capacity</th>
                <th className="px-4 py-3">Base</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {yachts.map((y) => (
                <tr
                  key={y.id}
                  className="border-b border-hbe-line/50 last:border-0"
                  data-testid={`yl-row-${y.slug}`}
                >
                  <td className="px-4 py-3">
                    {y.images?.[0] ? (
                      <img
                        src={assetUrl(y.images[0])}
                        alt=""
                        className="w-14 h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-14 h-10 bg-hbe-soft rounded flex items-center justify-center text-hbe-accent">
                        <ImageIcon size={14} />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-hbe-primary">
                      {y.name || <span className="text-hbe-ink/45">Untitled slot</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-hbe-ink/70">{y.slug}</td>
                  <td className="px-4 py-3">{y.capacity}</td>
                  <td className="px-4 py-3">{y.base_price ? formatMoney(y.base_price) : "—"}</td>
                  <td className="px-4 py-3">{y.display_order}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={y.is_archived ? "archived" : y.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/yachts/${y.id}`}
                      className="text-sm text-hbe-primary hover:underline"
                      data-testid={`yl-edit-${y.slug}`}
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
